import React, { useEffect, useState } from 'react';
import './MyAppsScreenDetail.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import backIcon from '../assets/backspace-icon.png';
import placeholderLogo from '../assets/placeholder-logo.png';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getStatusLabel(status) {
  if (!status) return 'draft';
  return String(status).toLowerCase();
}

function getVerifyLabel(status) {
  if (!status) return 'pending';
  return String(status).toLowerCase();
}

function MyAppsScreenDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { appId } = useParams();

  const [app, setApp] = useState(location.state?.app || null);
  const [loading, setLoading] = useState(!location.state?.app);
  const [error, setError] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (app) return;

    const fetchApp = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await api.get('/apps/me');
        const appProfiles = Array.isArray(res.data?.appProfiles) ? res.data.appProfiles : [];
        const matchedApp = appProfiles.find((item) => item._id === appId);

        if (!matchedApp) {
          setError('App not found.');
          return;
        }

        setApp(matchedApp);
      } catch (err) {
        console.error('❌ Failed to load app details:', err);
        setError(err?.response?.data?.message || 'Failed to load app details.');
      } finally {
        setLoading(false);
      }
    };

    fetchApp();
  }, [app, appId]);

  const handleDeactivate = async () => {
    if (!app?._id) return;

    const confirmed = window.confirm(
      'Deactivate this app? It will no longer remain active, but its history will be preserved.'
    );

    if (!confirmed) return;

    try {
      setDeactivating(true);

      await api.delete(`/apps/${app._id}`);

      alert('App de-activated successfully.');

      navigate('/my-apps', {
        state: {
          refreshApps: true,
        },
      });
    } catch (err) {
      console.error('❌ Failed to deactivate app:', err);
      alert(err?.response?.data?.message || 'Failed to deactivate app.');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="myappdetail-screen">
        <div className="myappdetail-topbar">
          <button className="myappdetail-back-btn" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="Back" />
          </button>
          <h1>App Details</h1>
        </div>

        <div className="myappdetail-content">
          <div className="myappdetail-card">
            <p>Loading app details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="myappdetail-screen">
        <div className="myappdetail-topbar">
          <button className="myappdetail-back-btn" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="Back" />
          </button>
          <h1>App Details</h1>
        </div>

        <div className="myappdetail-content">
          <div className="myappdetail-card error">
            <p>{error || 'App not found.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const status = getStatusLabel(app.status);
  const verificationStatus = getVerifyLabel(app.domainVerificationStatus);

  return (
    <div className="myappdetail-screen">
      <div className="myappdetail-topbar">
        <button className="myappdetail-back-btn" onClick={() => navigate(-1)}>
          <img src={backIcon} alt="Back" />
        </button>
        <h1>App Details</h1>
      </div>

      <div className="myappdetail-content">
        <div className="myappdetail-card">
          <div className="myappdetail-header">
            <img
              src={app.profilePic || placeholderLogo}
              alt={app.name || 'App logo'}
              className="myappdetail-logo"
            />

            <div className="myappdetail-header-meta">
              <h2>{app.name || 'Untitled App'}</h2>
              <p>@{app.username || 'no_username'}</p>
              <p className="myappdetail-domain">{app.domain || app.appUrl || '—'}</p>

              <div className="myappdetail-badges">
                <div className={`app-badge status-${status}`}>{status}</div>
                <div className={`app-badge verify-${verificationStatus}`}>{verificationStatus}</div>
                {app.linkedUser ? <div className="app-badge linked-ready">sub-account ready</div> : null}
              </div>
            </div>
          </div>

          <div className="myappdetail-info-grid">
            <div className="myappdetail-info-item">
              <span>App Name</span>
              <strong>{app.name || '—'}</strong>
            </div>

            <div className="myappdetail-info-item">
              <span>App Username</span>
              <strong>@{app.username || '—'}</strong>
            </div>

            <div className="myappdetail-info-item">
              <span>App URL</span>
              <strong>{app.appUrl || '—'}</strong>
            </div>

            <div className="myappdetail-info-item">
              <span>Domain</span>
              <strong>{app.domain || '—'}</strong>
            </div>

            <div className="myappdetail-info-item">
              <span>Category</span>
              <strong>{app.category || '—'}</strong>
            </div>

            <div className="myappdetail-info-item">
              <span>Created</span>
              <strong>{formatDate(app.createdAt)}</strong>
            </div>

            <div className="myappdetail-info-item full">
              <span>Bio</span>
              <strong>{app.bio || '—'}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="myappdetail-footer">
        <button
          className="myappdetail-danger-btn"
          onClick={handleDeactivate}
          disabled={deactivating}
        >
          {deactivating ? 'De-Activating...' : 'De-Activate App'}
        </button>
      </div>
    </div>
  );
}

export default MyAppsScreenDetail;
